import { API_BASE } from './api-base';

const getHeaders = (token?: string): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

const parseJsonResponse = async (response: Response) => {
  const rawBody = await response.text();
  if (!rawBody) {
    return {};
  }

  try {
    return JSON.parse(rawBody);
  } catch {
    const preview = rawBody.slice(0, 120).replace(/\s+/g, ' ').trim();
    throw new Error(
      `Expected JSON response but received invalid data (HTTP ${response.status}). Response preview: ${preview || '[empty]'}`
    );
  }
};

const getApiErrorMessage = (response: Response, data: any, fallback: string) => {
  if (data && typeof data.error === 'string' && data.error.trim()) {
    return data.error;
  }

  if (response.status === 404) {
    return `${fallback} Endpoint not found (HTTP 404). Ensure backend API is running on ${API_BASE}.`;
  }

  return fallback;
};

export const authApi = {
  initSuperAdmin: async (username: string, password: string) => {
    const response = await fetch(`${API_BASE}/auth/init-super-admin`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ username, password }),
    });

    const data = await parseJsonResponse(response);
    if (!response.ok) {
      throw new Error(getApiErrorMessage(response, data, 'Failed to initialize super admin.'));
    }

    return data;
  },

  superAdminLogin: async (username: string, password: string) => {
    try {
      const response = await fetch(`${API_BASE}/auth/super-admin-login`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ username, password }),
      });

      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(getApiErrorMessage(response, data, 'Login failed.'));
      }

      return data.session;
    } catch (error: any) {
      console.error('Super admin login error:', error);
      throw error;
    }
  },

  clientLogin: async (username: string, password: string) => {
    const response = await fetch(`${API_BASE}/auth/client-login`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ username, password }),
    });

    const data = await parseJsonResponse(response);
    if (!response.ok) {
      throw new Error(getApiErrorMessage(response, data, 'Login failed.'));
    }

    return { session: data.session, client: data.client };
  },

  unifiedLogin: async (username: string, password: string) => {
    try {
      const response = await fetch(`${API_BASE}/auth/unified-login`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ username, password }),
      });

      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(getApiErrorMessage(response, data, 'Login failed.'));
      }

      return { session: data.session, client: data.client };
    } catch (error: any) {
      console.error('Unified login error:', error);
      throw error;
    }
  },

  logout: async (token: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/logout`, {
        method: 'POST',
        headers: getHeaders(token),
      });

      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(getApiErrorMessage(response, data, 'Logout failed.'));
      }

      return data;
    } catch (error: any) {
      console.error('Logout error:', error);
      throw error;
    }
  },

  verifySession: async (token: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/verify`, {
        method: 'GET',
        headers: getHeaders(token),
      });

      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(getApiErrorMessage(response, data, 'Session verification failed.'));
      }

      // Ensure session always has complete data including permissions
      const session = data.session || data;
      return {
        ...session,
        user: {
          ...session.user,
          // Ensure permissions object exists even if backend doesn't return it
          permissions: session.user?.permissions || {},
        }
      };
    } catch (error: any) {
      console.error('Verify session error:', error);
      throw error;
    }
  },
};

// Session storage helpers
const sanitizeSessionForStorage = (session: any) => {
  if (!session) return null;
  
  // Only store essential fields - minimize size to avoid localStorage quota
  const sanitized = {
    token: session.token,
    user: session.user ? {
      id: session.user.id,
      email: session.user.email,
      role: session.user.role,
      clientId: session.user.clientId,
      permissions: session.user.permissions,
    } : undefined,
    role: session.role,
    clientId: session.clientId,
    client: session.client ? {
      id: session.client.id,
      name: session.client.name,
      pollingIntervalMs: session.client.pollingIntervalMs,
    } : undefined,
    licenseExpired: session.licenseExpired,
    licenseExpiresAt: session.licenseExpiresAt,
    daysUntilExpiration: session.daysUntilExpiration,
  };
  
  return sanitized;
};

export const sessionStorage = {
  save: (session: any) => {
    try {
      const sanitized = sanitizeSessionForStorage(session);
      const json = JSON.stringify(sanitized);
      localStorage.setItem('factreon_session', json);

      // Compatibility keys used by legacy components.
      const token = session?.token || sanitized?.token;
      const role = session?.user?.role || session?.role || '';
      const clientId = session?.user?.clientId || session?.clientId || '';
      const clientName = session?.client?.name || session?.clientName || '';

      if (token) {
        localStorage.setItem('token', token);
        localStorage.setItem('auth_token', token);
        localStorage.setItem('authToken', token);
        window.sessionStorage.setItem('token', token);
        window.sessionStorage.setItem('authToken', token);
      }

      if (clientId) {
        localStorage.setItem('client_id', String(clientId));
      }

      if (clientName) {
        window.sessionStorage.setItem('clientName', String(clientName));
      }

      if (role) {
        window.sessionStorage.setItem('role', String(role));
      }

      console.log('✅ Session saved, size:', json.length, 'bytes');
    } catch (error) {
      console.error('Failed to save session:', error);
      // If quota exceeded, avoid destructive global clear and retry minimal writes.
      if ((error as any)?.name === 'QuotaExceededError') {
        console.warn('⚠️ localStorage quota exceeded');
        try {
          localStorage.removeItem('factreon_session');
          localStorage.setItem('factreon_session', JSON.stringify(sanitizeSessionForStorage(session)));
          console.log('✅ Session saved after clearing old data');
        } catch (retryError) {
          console.error('Failed to save session even after clearing:', retryError);
        }
      }
    }
  },

  get: () => {
    try {
      const session = localStorage.getItem('factreon_session');
      return session ? JSON.parse(session) : null;
    } catch (error) {
      console.error('Failed to parse session, clearing:', error);
      localStorage.removeItem('factreon_session');
      return null;
    }
  },

  clear: () => {
    try {
      localStorage.removeItem('factreon_session');
      localStorage.removeItem('token');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('authToken');
      localStorage.removeItem('client_id');
      window.sessionStorage.removeItem('token');
      window.sessionStorage.removeItem('authToken');
      window.sessionStorage.removeItem('clientName');
      window.sessionStorage.removeItem('role');
    } catch (error) {
      console.error('Failed to clear session:', error);
    }
  },
};

// Theme storage
export const themeStorage = {
  save: (theme: 'light' | 'dark') => {
    localStorage.setItem('factreon_theme', theme);
  },

  get: (): 'light' | 'dark' => {
    return (localStorage.getItem('factreon_theme') as 'light' | 'dark') || 'dark';
  },
};
